import React, { useRef, useState } from 'react';
import { Pressable, ScrollView, View, type ScrollViewInstance } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { P, type AssistantReply } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/errors';
import { Avatar, Button, Icon, Loading, PageHeader, radius, Screen, Text, TextField, useTheme } from '../../../ui';

type Entry = { me: boolean; text: string; reply?: AssistantReply; error?: boolean };

function Chips({ items, onPick, center }: { items: string[]; onPick: (s: string) => void; center?: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, justifyContent: center ? 'center' : 'flex-start' }}>
      {items.map((s) => (
        <Pressable key={s} accessibilityRole="button" onPress={() => onPick(s)} style={({ pressed }) => ({ borderWidth: 1, borderColor: pressed ? colors.primary : colors.border, backgroundColor: colors.surface, borderRadius: radius.pill, paddingHorizontal: 12, paddingVertical: 6 })}>
          <Text size={12}>{s}</Text>
        </Pressable>
      ))}
    </View>
  );
}

function DataPreview({ data }: { data: unknown }) {
  const { colors } = useTheme();
  if (!Array.isArray(data) || !data.length || typeof data[0] !== 'object' || data[0] === null) return null;
  const cols = Object.keys(data[0] as object).slice(0, 6);
  return (
    <ScrollView horizontal style={{ marginTop: 8 }}>
      <View>
        <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: colors.border }}>
          {cols.map((c) => (
            <Text key={c} size={12} weight="semibold" style={{ width: 110, padding: 4 }}>
              {c}
            </Text>
          ))}
        </View>
        {data.slice(0, 20).map((r, i) => (
          <View key={i} style={{ flexDirection: 'row' }}>
            {cols.map((c) => (
              <Text key={c} size={12} style={{ width: 110, padding: 4 }} numberOfLines={1}>
                {String((r as Record<string, unknown>)[c] ?? '')}
              </Text>
            ))}
          </View>
        ))}
      </View>
    </ScrollView>
  );
}

export function AssistantScreen() {
  const { me, can } = useAuth();
  const { colors } = useTheme();
  const [log, setLog] = useState<Entry[]>([]);
  const [text, setText] = useState('');
  const scroller = useRef<ScrollViewInstance>(null);
  const ask = useMutation({
    mutationFn: (q: string) => api.ess.ask(q),
    onSuccess: (r) => setLog((l) => [...l, { me: false, text: r.answer, reply: r }]),
    onError: (e) => setLog((l) => [...l, { me: false, text: errorMessage(e), error: true }]),
  });
  const send = (q: string) => {
    if (!q.trim() || ask.isPending) return;
    setLog((l) => [...l, { me: true, text: q }]);
    setText('');
    ask.mutate(q);
  };
  const starters = [
    "What's my leave balance?",
    'When is the next holiday?',
    'How much TDS was deducted this year?',
    ...(can(P.REPORTS_FULL, P.EMPLOYEES_VIEW) ? ['Show attrition by department', 'Headcount by location'] : []),
    ...(can(P.EMPLOYEES_TEAM) ? ['Who in my team is on leave today?'] : []),
  ];
  const lastSuggestions = [...log].reverse().find((e) => e.reply?.suggestions?.length)?.reply?.suggestions;

  return (
    <Screen scroll={false} maxWidth={900}>
      <PageHeader title="AI assistant" subtitle="Ask about your leave, attendance, pay and company policies." />
      <View style={{ flex: 1, backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' }}>
        <ScrollView ref={scroller} style={{ flex: 1 }} contentContainerStyle={{ padding: 16, gap: 14, flexGrow: 1 }} onContentSizeChange={() => scroller.current?.scrollToEnd({ animated: true })}>
          {log.length === 0 && (
            <View style={{ margin: 'auto', alignItems: 'center', gap: 12, maxWidth: 480, paddingVertical: 24 }}>
              <View style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: colors.primarySoft, alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="bot" size={26} color={colors.primary} />
              </View>
              <Text variant="h3" align="center">
                How can I help, {me?.name.split(' ')[0]}?
              </Text>
              <Chips items={starters} onPick={send} center />
            </View>
          )}
          {log.map((e, i) => (
            <View key={i} style={{ flexDirection: e.me ? 'row-reverse' : 'row', gap: 10, alignItems: 'flex-start' }}>
              {e.me ? (
                <Avatar name={me?.name ?? 'Me'} size={30} />
              ) : (
                <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: colors.primarySoft, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="bot" size={16} color={colors.primary} />
                </View>
              )}
              <View style={{ maxWidth: '85%', paddingHorizontal: 14, paddingVertical: 10, borderRadius: 14, backgroundColor: e.me ? colors.primary : colors.surface2, borderWidth: e.me ? 0 : 1, borderColor: colors.border }}>
                <Text color={e.error ? colors.danger : e.me ? colors.onPrimary : colors.text}>{e.text}</Text>
                {e.reply?.data !== undefined && <DataPreview data={e.reply.data} />}
              </View>
            </View>
          ))}
          {ask.isPending && <Loading inline label="Thinking…" />}
          {lastSuggestions && !ask.isPending && <Chips items={lastSuggestions} onPick={send} />}
        </ScrollView>
        <View style={{ flexDirection: 'row', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: colors.border, alignItems: 'center' }}>
          <TextField containerStyle={{ flex: 1 }} placeholder="Ask anything… e.g. How many casual leaves do I have?" accessibilityLabel="Ask a question" value={text} onChangeText={setText} onSubmitEditing={() => send(text)} returnKeyType="send" />
          <Button variant="primary" icon="send" title="Send" disabled={!text.trim() || ask.isPending} onPress={() => send(text)} />
        </View>
      </View>
    </Screen>
  );
}
